import { unique } from "@foretagsplatsen/core-helpers/array";
import UndefinedAccount from "../UndefinedAccount.js";

/**
 * `interpretationContext` contains report settings affecting
 * interpretation of a report template to a report.
 *
 * @param {reportTemplate} spec.reportTemplate - Report template to
 * be interpreted.
 * @param {chartOfAccounts} spec.chartOfAccounts -
 * @param {company} spec.company -
 * @param {specification} [spec.dimensionSpecification] -
 * @param {keyFigures} spec.keyFigures -
 * @param {period} spec.period -
 * @param {period[]} [spec.repeatingPeriods] - Periods over which
 * to repeat the report template columns.
 * @param {Map} [spec.templateSettings] - Maps a template to a
 * literal object representing interpretation settings for this
 * template (e.g., dimensions and budget).
 */
export default class InterpretationContext extends Object {
	constructor({
		reportTemplate,
		chartOfAccounts,
		company,
		dimensionSpecification,
		keyFigures = [],
		period,
		repeatingPeriods = [],
		templateSettings = new Map(),
	}) {
		super(...arguments);

		this._reportTemplate = reportTemplate;
		this._chartOfAccounts = chartOfAccounts;
		this._company = company;
		this._dimensionSpecification = dimensionSpecification;
		this._keyFigures = keyFigures;
		this._period = period;
		this._repeatingPeriods = repeatingPeriods;
		this._templateSettings = templateSettings;
		// add new fields to the clone() method as well.

		this._cache = {};
	}

	/**
	 * Creates a new interpretation context based on the
	 * properties of the receiver. Differences between the new
	 * context and the receiver can be expressed in `spec`.
	 */
	clone(spec = {}) {
		const newSpec = {
			reportTemplate: this._reportTemplate,
			chartOfAccounts: this._chartOfAccounts,
			company: this._company,
			dimensionSpecification: this._dimensionSpecification,
			keyFigures: this._keyFigures,
			period: this._period,
			repeatingPeriods: this._repeatingPeriods,
			templateSettings: this._templateSettings,
			...spec,
		};
		return new this.constructor(newSpec);
	}

	/**
	 * Creates a new interpretation context based on the
	 * properties of the receiver. The parameter
	 * `additionalSettings` is a map similar to `templateSettings`
	 * passed to the constructor.
	 *
	 * Settings are merged together in the new instance. On the
	 * contrary, calling `clone()` with some settings will
	 * completely replace existing settings by new ones.
	 */
	cloneWithAdditionalSettings(additionalSettings) {
		const newSettings = new Map(this._templateSettings);

		for (const template of additionalSettings.keys()) {
			const originalTemplateSettings =
				this._templateSettings.get(template) || {};
			const additionalTemplateSettings =
				additionalSettings.get(template) || {};

			const newTemplateSettings = {
				...originalTemplateSettings,
				...additionalTemplateSettings,
			};
			newSettings.set(template, newTemplateSettings);
		}

		return this.clone({ templateSettings: newSettings });
	}

	getReportTemplate() {
		return this._reportTemplate;
	}

	getCompany() {
		return this._company;
	}

	getPeriod() {
		return this._period;
	}

	setPeriod(period) {
		this._period = period;
	}

	getFiscalYear() {
		return this._period.getYear();
	}

	getRepeatingPeriods() {
		return this._repeatingPeriods;
	}

	getChartOfAccounts() {
		return this._chartOfAccounts;
	}

	getDimensionSpecification() {
		return this._dimensionSpecification;
	}

	getKeyFigures() {
		return this._keyFigures;
	}

	getVisibleKeyFigures() {
		return this.getKeyFigures().filter((keyFigure) =>
			keyFigure.isVisible(),
		);
	}

	/**
	 * Return a set of all accounts from all years.
	 *
	 * - Removes duplicated accounts (accounts with the same number).
	 *
	 * - In case several accounts from different years share the same number,
	 * pick the one for the receiver's period
	 */
	getAccounts() {
		if (this._accountsCache) {
			return this._accountsCache;
		}

		const primaryAccounts = this._period.getAccounts();
		const allAccounts = this._company
			.getYears()
			.flatMap((year) => year.getAccounts());

		this._accountsCache = unique(
			[...primaryAccounts, ...allAccounts],
			(account) => account.getNumber(),
		).sort((accountA, accountB) => accountA.compareTo(accountB));

		return this._accountsCache;
	}

	getDimensionTree() {
		return this._period.getDimensionTree();
	}

	getDimensionSpecificationForTemplate(template) {
		const templateSettings = this._templateSettings.get(template);
		return templateSettings && templateSettings.dimensionSpecification;
	}

	getBudgetIdForTemplate(template) {
		const templateSettings = this._templateSettings.get(template);
		return templateSettings && templateSettings.budgetId;
	}

	findKeyFigure(id) {
		return this._keyFigures.find((keyFigure) => keyFigure.getId() === id);
	}

	findYear(predicate) {
		return this._company.getYears().find(predicate);
	}

	/**
	 * Search for an account matching `accountNumber`. If not
	 * found, returns `undefinedAccount` with `accountNumber`.
	 */
	findAccount(accountNumber) {
		const account = this._getAccountsById()[accountNumber];
		return account || new UndefinedAccount({ number: accountNumber });
	}

	findDimension(dimensionId) {
		return this.getDimensionTree().findById(dimensionId);
	}

	findDimensions(dimensionIds) {
		return this.getDimensionTree().findAllByIds(dimensionIds);
	}

	_getAccountsById() {
		if (!this._accountsById) {
			this._accountsById = {};
			const accounts = this.getAccounts();

			accounts.forEach((account) => {
				this._accountsById[account.getNumber()] = account;
			});
		}

		return this._accountsById;
	}
}
